/**
 * /docs/prompting — patterns that consistently produce apps worth keeping.
 * Path-agnostic; the "magic word" section calls out where Claude Code
 * differs from the in-browser chat (which doesn't need it).
 */

import { PageHeader } from '../../components/ui.js';
import { DocsTabs } from '../../components/DocsTabs.js';
import { Callout, CodeBlock, DocSection } from './_shared.js';

export function DocsPrompting() {
  return (
    <div className="animate-fade-in">
      <PageHeader
        eyebrow="Docs · Prompting"
        title="How to prompt Kin well"
        description="Clear prompts produce dramatically better apps. These are the patterns that hold up."
      />

      <DocsTabs />

      <DocSection title="When &ldquo;Kin&rdquo; matters (and when it doesn't)">
        <p>
          <strong>In the browser:</strong> the chat bubble in your app&apos;s preview already knows
          it&apos;s editing a Kin app. Skip the word, just say what you want.
        </p>
        <CodeBlock
          body={`Add a column for dietary notes.
Make the submit button bigger on phones.
Promote this to live.`}
          caption="in-browser — no Kin needed"
          noCopy
        />
        <p>
          <strong>In Claude Code:</strong> the skill is gated on the word <em>&ldquo;Kin&rdquo;</em>{' '}
          (or <span className="font-mono text-xs">mkin.app</span>). Without it, Claude defaults to
          general code-writing mode and might write a Next.js project on your laptop instead of
          deploying to Kin.
        </p>
        <CodeBlock
          body={`Make me a Kin app to ...
Build a Kin tool that ...
Deploy this to mkin.app — ...`}
          caption="Claude Code — activates the skill"
          noCopy
        />
        <Callout tone="tip" title="Once Kin is engaged, drop it">
          <p>
            In Claude Code you only need the word in the first message. After any{' '}
            <span className="font-mono text-xs">kin_*</span> tool call, follow-ups can be anything:
            &ldquo;add a filter,&rdquo; &ldquo;ship it,&rdquo; &ldquo;rename the slug.&rdquo;
          </p>
        </Callout>
      </DocSection>

      <DocSection title="Describe the problem, not the solution">
        <p>The agent is better at picking the right shape than you are. Trust it.</p>
        <p>
          <strong>Do:</strong>
        </p>
        <CodeBlock
          body={`We have a weekly all-hands and people forget who's presenting. Build a tool
that shows the next four weeks' rotation, lets a manager edit it, and emails
the presenter at 8am the day-of.`}
          caption="problem-shaped"
        />
        <p>
          <strong>Don&apos;t:</strong>
        </p>
        <CodeBlock
          body={`Build a React app with a Postgres backend, JWT auth, an admin panel, role-based
permissions, and a cron worker that triggers an email via SendGrid at 8am UTC ...`}
          caption="over-specified"
        />
        <p>
          The second is louder but worse. It pins the agent to choices Kin can&apos;t satisfy
          (Postgres? SendGrid?) and skips the actual logic.
        </p>
      </DocSection>

      <DocSection title="Iterate, don't restart">
        <p>Preview deploys take seconds. New apps take minutes and lose your data.</p>
        <CodeBlock
          body={`The date picker only goes one week out — let me pick any date this month.
Also the "submit" button is way too small on my phone.`}
          caption="targeted follow-up"
        />
        <p>
          The exception: switching <em>needs_database</em> after the fact. Kin can&apos;t mutate
          that. The agent will flag it when you hit it.
        </p>
      </DocSection>

      <DocSection title="In the browser: point at things you can't describe">
        <p>
          Click the crosshair icon next to the chat input, then click any element on the preview.
          The agent gets a reference to that element &mdash; useful for &ldquo;this button,&rdquo;{' '}
          &ldquo;the row at the top,&rdquo; or layout problems that would take a paragraph to
          describe in words.
        </p>
        <p className="text-muted">
          Claude Code can&apos;t see the page, so use words there: file names, CSS selectors, screen
          positions.
        </p>
      </DocSection>

      <DocSection title="Never paste secrets in chat">
        <p>
          API keys, tokens, passwords, customer data &mdash; chat is not the place. Kin has a
          Secrets panel for a reason.
        </p>
        <ol className="list-decimal list-inside space-y-1 pl-1">
          <li>
            Ask:{' '}
            <em>
              &ldquo;set this up to use a Linear API key called{' '}
              <span className="font-mono text-xs">LINEAR_KEY</span>.&rdquo;
            </em>
          </li>
          <li>The agent writes code referencing that name. It doesn&apos;t need the value.</li>
          <li>
            You open <strong>your app &rarr; Secrets</strong> and paste the value. Encrypted at
            rest, never logged.
          </li>
          <li>Next deploy picks it up.</li>
        </ol>
      </DocSection>

      <DocSection title="Ask for what you actually need">
        <p>The agent biases toward minimal. Tell it what to include &mdash; and what to skip.</p>
        <ul className="list-disc list-inside space-y-1 pl-1">
          <li>
            <strong>Access:</strong> &ldquo;anyone at Mytra&rdquo;, &ldquo;just me,&rdquo; or
            &ldquo;these three people.&rdquo;
          </li>
          <li>
            <strong>Look &amp; feel:</strong> &ldquo;match Mytra brand,&rdquo;
            &ldquo;minimal,&rdquo; &ldquo;dark by default.&rdquo;
          </li>
          <li>
            <strong>Data shape:</strong> &ldquo;each entry has a title, an owner, a status (open /
            done), and a date.&rdquo;
          </li>
          <li>
            <strong>Behaviors:</strong> &ldquo;email me when someone adds a new entry,&rdquo;
            &ldquo;reset every Monday.&rdquo;
          </li>
        </ul>
      </DocSection>

      <DocSection title="Use the portal to manage; the agent to build">
        <p>Permanent decisions about who sees what belong in the portal, not in chat.</p>
        <ul className="list-disc list-inside space-y-1 pl-1">
          <li>Adding or removing specific people from share lists.</li>
          <li>Flipping an app between &ldquo;anyone at Mytra&rdquo; and private.</li>
          <li>Pasting secret values.</li>
          <li>Restoring an exact version by number.</li>
        </ul>
        <p>
          All of those are one click in the portal &mdash; the app&apos;s side tabs (Access,
          Secrets, Versions).
        </p>
      </DocSection>

      <DocSection title="Tell the agent when you don't understand">
        <p>Some prompts that get plain-English answers back:</p>
        <CodeBlock
          body={`What does "promote to live" mean exactly?
Why did you make this a backend app instead of just static HTML?
Show me what the data looks like right now.
What happens if I delete the preview — does live still work?`}
          caption="follow-up questions"
          noCopy
        />
      </DocSection>
    </div>
  );
}
